import { Link } from "react-router-dom";
import { ArrowRight, Check, UserPlus, GraduationCap, CalendarCheck, Heart, BookOpen } from "lucide-react";
import { TOOLS, PRICING_TIERS } from "@/lib/toolsContent";
import ToolMock from "@/components/marketing/ToolMock";
import DemoBanner from "@/components/marketing/DemoBanner";

function Hero() {
  return (
    <section className="relative overflow-hidden" data-testid="home-hero">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60rem 30rem at 85% -10%, rgba(230,184,92,0.14), transparent 60%), radial-gradient(40rem 24rem at -10% 110%, rgba(59,130,246,0.10), transparent 60%)" }} />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 md:pt-24 pb-14 relative">
        <div className="overline mb-6 home-rise" style={{ color: "var(--gold)" }}>Church tools by The Disciple Life</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-[-0.045em] max-w-5xl home-rise" style={{ color: "var(--ink)", animationDelay: "80ms" }}>
          Turn first-time guests into <span style={{ color: "var(--gold)" }}>lifelong disciples.</span>
        </h1>
        <p className="mt-6 text-base md:text-lg max-w-2xl leading-relaxed home-rise" style={{ color: "var(--ink-2)", animationDelay: "160ms" }}>
          One simple platform for your Bible-study pipeline, attendance, prayer, lobby check-in and your church's own social feed. Built by people who disciple, for the people in your church who do the discipling.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 home-rise" style={{ animationDelay: "240ms" }}>
          <Link to="/demo" className="btn-primary" data-testid="hero-demo-btn">Request a demo <ArrowRight size={15} /></Link>
          <a href="#tools" className="btn-ghost" data-testid="hero-explore-btn">Explore the tools</a>
        </div>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-3 home-rise" style={{ animationDelay: "320ms" }} data-testid="hero-tool-strip">
          {TOOLS.map((t) => (
            <Link key={t.slug} to={`/tools/${t.slug}`} className="group rounded-lg px-4 py-3 flex items-center justify-between gap-2 transition-colors duration-200" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid={`hero-tool-${t.slug}`}>
              <span className="min-w-0">
                <span className="block text-[0.55rem] uppercase tracking-[0.2em]" style={{ color: t.accent }}>{t.kicker}</span>
                <span className="block font-serif-display text-lg" style={{ color: "var(--ink)" }}>{t.name}</span>
              </span>
              <ArrowRight size={14} className="shrink-0 transition-transform duration-200 group-hover:translate-x-1" style={{ color: t.accent }} />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ToolSection({ tool, index }) {
  const flip = index % 2 === 1;
  return (
    <section id={tool.slug} className="max-w-7xl mx-auto px-6 lg:px-12 py-14 md:py-20" data-testid={`home-tool-${tool.slug}`}>
      <div className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}>
        <div className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.22em] px-3 py-1 rounded-full" style={{ background: tool.glow, color: tool.accent }}>
            {tool.brand} · {tool.included}
          </div>
          <h2 className="font-serif-display text-3xl md:text-5xl tracking-[-0.03em] leading-[1.05] mt-4" style={{ color: "var(--ink)" }}>{tool.headline}</h2>
          <p className="mt-4 text-sm md:text-base leading-relaxed max-w-xl" style={{ color: "var(--ink-2)" }}>{tool.pitch}</p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
            {tool.features.slice(0, 4).map((f) => (
              <li key={f.title} className="flex items-start gap-2 text-sm" style={{ color: "var(--ink)" }}>
                <Check size={15} className="mt-0.5 shrink-0" style={{ color: tool.accent }} /> {f.title}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/tools/${tool.slug}`} className="btn-ghost" data-testid={`home-tool-learn-${tool.slug}`}>Learn more about {tool.name} <ArrowRight size={14} /></Link>
            <Link to="/demo" className="text-xs uppercase tracking-[0.2em] self-center hover:underline" style={{ color: tool.accent }} data-testid={`home-tool-demo-${tool.slug}`}>Request a demo</Link>
          </div>
        </div>
        <div className="lg:col-span-6 relative">
          <div className="absolute -inset-6 rounded-3xl pointer-events-none" style={{ background: `radial-gradient(closest-side, ${tool.glow}, transparent)` }} />
          <div className="relative transition-transform duration-300 hover:-translate-y-1"><ToolMock tool={tool} /></div>
        </div>
      </div>
    </section>
  );
}

function Workflow() {
  const steps = [
    { icon: UserPlus, label: "A guest signs up at the kiosk", color: "#10B981" },
    { icon: GraduationCap, label: "The Tracker pairs them with a teacher", color: "#E6B85C" },
    { icon: CalendarCheck, label: "They join a section in Attendance", color: "#3B82F6" },
    { icon: Heart, label: "They become one who prays", color: "#EC4899" },
  ];
  return (
    <section style={{ background: "var(--bg-deep)", borderTop: "1px solid var(--border-subtle)", borderBottom: "1px solid var(--border-subtle)" }} data-testid="home-workflow">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 md:py-20">
        <div className="overline mb-3" style={{ color: "var(--gold)" }}>One directory, four tools</div>
        <h2 className="font-serif-display text-3xl md:text-4xl tracking-[-0.03em] max-w-2xl" style={{ color: "var(--ink)" }}>Every person, followed from the front door to the prayer room.</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-4">
          {steps.map(({ icon: Icon, label, color }, i) => (
            <div key={label} className="relative p-5 rounded-lg" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}>
              <span className="text-[0.6rem] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>Step {i + 1}</span>
              <Icon size={22} className="mt-3" style={{ color }} />
              <p className="mt-3 text-sm" style={{ color: "var(--ink)" }}>{label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSnippet() {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 md:py-24" data-testid="home-pricing">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-5">
          <div className="overline mb-3" style={{ color: "var(--gold)" }}>Simple pricing</div>
          <h2 className="font-serif-display text-3xl md:text-5xl tracking-[-0.03em] leading-[1.05]" style={{ color: "var(--ink)" }}>Priced by your church's size, not by seats.</h2>
          <p className="mt-4 text-sm md:text-base" style={{ color: "var(--ink-2)" }}>Every church starts with the Tracker. Add Attendance, Prayer or Kiosk whenever you're ready. Unlimited teachers and leaders, monthly billing, cancel anytime.</p>
          <Link to="/pricing" className="btn-ghost mt-6" data-testid="home-pricing-link">See full pricing <ArrowRight size={14} /></Link>
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 gap-3">
          {PRICING_TIERS.map((t) => (
            <div key={t.label} className="p-5 rounded-lg" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}>
              <div className="text-[0.6rem] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>{t.label} · {t.range}</div>
              <div className="font-serif-display text-4xl mt-2" style={{ color: "var(--ink)" }}>${t.tracker}<span className="text-sm" style={{ color: "var(--muted)" }}> /mo</span></div>
              <div className="text-xs mt-1" style={{ color: "var(--ink-2)" }}>Add-ons +${t.addon}/mo each</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MinistryBanner() {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12" data-testid="home-ministry-banner">
      <Link to="/ministry" className="group flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 md:p-8 rounded-xl transition-colors" style={{ border: "1px solid var(--border-subtle)" }}>
        <span className="flex items-center gap-4">
          <BookOpen size={22} style={{ color: "var(--gold)" }} />
          <span>
            <span className="block font-serif-display text-xl md:text-2xl" style={{ color: "var(--ink)" }}>Looking for the ministry?</span>
            <span className="block text-sm mt-1" style={{ color: "var(--ink-2)" }}>Journal, books, Read With Me, counseling and membership are all in one place.</span>
          </span>
        </span>
        <span className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--gold)" }}>Visit the ministry <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
      </Link>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <div id="tools">
        {TOOLS.map((t, i) => <ToolSection key={t.slug} tool={t} index={i} />)}
      </div>
      <Workflow />
      <PricingSnippet />
      <MinistryBanner />
      <DemoBanner />
    </div>
  );
}
